import { useEffect, useRef, useState, type CSSProperties, type ReactElement, type ReactNode } from "react";
import { palette, radius, space, typography } from "@ux/tokens";

export interface DataColumn<TRow> {
    readonly id: string;
    readonly header: string;
    readonly width?: string;
    readonly render: (row: TRow) => ReactNode;
}

export interface DataTableSelection {
    readonly selectedKeys: ReadonlySet<string>;
    readonly onChange: (next: ReadonlySet<string>) => void;
}

export interface DataTableProps<TRow> {
    readonly columns: readonly DataColumn<TRow>[];
    readonly rows: readonly TRow[];
    readonly rowKey: (row: TRow) => string;
    readonly selectedKey?: string | null;
    readonly onSelect?: (row: TRow) => void;
    readonly emptyContent?: ReactNode;
    readonly stripedTone?: "default" | "deleted";
    readonly isRowMuted?: (row: TRow) => boolean;
    /**
     * When set, a checkbox column is rendered so the caller can run a
     * bulk action on the checked rows. Checking a box does not open
     * the row; the row click still calls `onSelect`.
     */
    readonly selection?: DataTableSelection;
}

export function DataTable<TRow>({
    columns,
    rows,
    rowKey,
    selectedKey,
    onSelect,
    emptyContent,
    isRowMuted,
    selection,
}: DataTableProps<TRow>): ReactElement {
    if (rows.length === 0) {
        return (
            <div style={{ flex: 1, display: "flex", minHeight: 0 }}>
                {emptyContent ?? null}
            </div>
        );
    }

    const keys = rows.map((row) => rowKey(row));
    const selectedCount = selection === undefined ? 0 : keys.filter((key) => selection.selectedKeys.has(key)).length;
    const allChecked = selection !== undefined && selectedCount === keys.length && keys.length > 0;
    const someChecked = selection !== undefined && selectedCount > 0 && !allChecked;

    function toggleAll(): void {
        if (selection === undefined) return;
        const next = new Set(selection.selectedKeys);
        if (allChecked) {
            for (const key of keys) next.delete(key);
        } else {
            for (const key of keys) next.add(key);
        }
        selection.onChange(next);
    }

    function toggleOne(key: string): void {
        if (selection === undefined) return;
        const next = new Set(selection.selectedKeys);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        selection.onChange(next);
    }

    return (
        <div style={tableContainerStyle}>
            <table style={tableStyle}>
                <thead>
                    <tr>
                        {selection !== undefined ? (
                            <th style={{ ...thStyle, width: 44 }}>
                                <SelectAllBox
                                    checked={allChecked}
                                    indeterminate={someChecked}
                                    onChange={toggleAll}
                                />
                            </th>
                        ) : null}
                        {columns.map((column) => (
                            <th
                                key={column.id}
                                style={{
                                    ...thStyle,
                                    width: column.width,
                                }}
                            >
                                {column.header}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row) => {
                        const key = rowKey(row);
                        const selected = selectedKey === key;
                        const muted = isRowMuted?.(row) ?? false;
                        const checked = selection?.selectedKeys.has(key) ?? false;
                        const rowProps: RowProps<TRow> = {
                            columns,
                            row,
                            selected,
                            muted,
                            checked,
                            ...(selection !== undefined
                                ? { onToggle: () => toggleOne(key) }
                                : {}),
                            ...(onSelect ? { onClick: () => onSelect(row) } : {}),
                        };
                        return <Row<TRow> key={key} {...rowProps} />;
                    })}
                </tbody>
            </table>
        </div>
    );
}

interface RowProps<TRow> {
    readonly columns: readonly DataColumn<TRow>[];
    readonly row: TRow;
    readonly selected: boolean;
    readonly muted: boolean;
    readonly checked: boolean;
    readonly onToggle?: () => void;
    readonly onClick?: () => void;
}

function SelectAllBox({
    checked,
    indeterminate,
    onChange,
}: {
    readonly checked: boolean;
    readonly indeterminate: boolean;
    readonly onChange: () => void;
}): ReactElement {
    const ref = useRef<HTMLInputElement>(null);
    useEffect(() => {
        if (ref.current !== null) ref.current.indeterminate = indeterminate;
    }, [indeterminate]);

    return (
        <input
            ref={ref}
            type="checkbox"
            checked={checked}
            aria-label="Select all rows"
            onChange={onChange}
            style={checkboxStyle}
        />
    );
}

function Row<TRow>({
    columns,
    row,
    selected,
    muted,
    checked,
    onToggle,
    onClick,
}: RowProps<TRow>): ReactElement {
    const [hover, setHover] = useState(false);

    const background = selected
        ? palette.accentBg
        : hover && onClick !== undefined
          ? palette.surfaceRaised
          : "transparent";

    const trStyle: CSSProperties = {
        background,
        cursor: onClick !== undefined ? "pointer" : "default",
        opacity: muted ? 0.55 : 1,
        transition: "background 90ms ease",
    };

    return (
        <tr
            style={trStyle}
            onClick={onClick}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
        >
            {onToggle !== undefined ? (
                <td
                    style={tdStyle}
                    onClick={(event) => {
                        event.stopPropagation();
                    }}
                >
                    <input
                        type="checkbox"
                        checked={checked}
                        aria-label="Select row"
                        onChange={onToggle}
                        style={checkboxStyle}
                    />
                </td>
            ) : null}
            {columns.map((column) => (
                <td key={column.id} style={tdStyle}>
                    {column.render(row)}
                </td>
            ))}
        </tr>
    );
}

const tableContainerStyle: CSSProperties = {
    flex: 1,
    minHeight: 0,
    overflow: "auto",
};

const tableStyle: CSSProperties = {
    width: "100%",
    borderCollapse: "separate",
    borderSpacing: 0,
    fontSize: typography.size.sm,
};

const thStyle: CSSProperties = {
    position: "sticky",
    top: 0,
    // Sticky headers need a fully-opaque background so scrolling rows
    // don't bleed through. `palette.surface` is rgba white at 4% — fine
    // as a pane tint, useless as an overlay. Stack the same tint over
    // `palette.canvas` so the header visually matches the surrounding
    // pane while being opaque enough to read against scrolling content.
    background: `linear-gradient(${palette.surface}, ${palette.surface}), ${palette.canvas}`,
    color: palette.fgSubtle,
    fontWeight: typography.weight.semibold,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    fontSize: typography.size.xs,
    padding: `${space[2]} ${space[4]}`,
    textAlign: "left",
    borderBottom: `1px solid ${palette.border}`,
    zIndex: 1,
};

const checkboxStyle: CSSProperties = {
    width: 16,
    height: 16,
    accentColor: palette.accent,
    cursor: "pointer",
};

const tdStyle: CSSProperties = {
    padding: `${space[3]} ${space[4]}`,
    color: palette.fg,
    borderBottom: `1px solid ${palette.border}`,
    verticalAlign: "middle",
    borderRadius: radius.none,
};
